import type { CallHistoryItem } from '../../types';
import { isCallTerminalForControlPanel } from './liveControlVisibility';

export type TransferStateKey = 'connected' | 'inProgress' | 'pending' | 'ended' | 'unanswered';

export interface TransferStateSnapshot {
  key: TransferStateKey;
  chipColor: 'default' | 'success' | 'warning';
  isHistorical: boolean;
}

/**
 * Resolves the transfer badge state for the call detail drawer.
 *
 * A transfer that started but never connected must stop rendering as
 * "in progress" once the call itself reaches a terminal boundary.
 */
export function getTransferStateSnapshot(
  call: Pick<
    CallHistoryItem,
    | 'status'
    | 'endedAt'
    | 'telephonyTransferStartedAt'
    | 'telephonyTransferConnectedAt'
    | 'transferDestinationType'
    | 'terminationReasonCode'
  >,
): TransferStateSnapshot | null {
  if (!call.transferDestinationType) return null;

  const isTerminal = isCallTerminalForControlPanel(call);
  const hasStarted = Boolean(call.telephonyTransferStartedAt);
  const hasConnected = Boolean(call.telephonyTransferConnectedAt);
  const isTransferUnanswered = call.status === 'transfer_unanswered'
    || call.terminationReasonCode === 'internal_transfer_unanswered';

  if (hasConnected) {
    return {
      key: 'connected',
      chipColor: 'success',
      isHistorical: isTerminal,
    };
  }

  if (isTerminal && hasStarted) {
    return {
      key: isTransferUnanswered ? 'unanswered' : 'ended',
      chipColor: isTransferUnanswered ? 'warning' : 'default',
      isHistorical: true,
    };
  }

  if (hasStarted) {
    return {
      key: 'inProgress',
      chipColor: 'warning',
      isHistorical: false,
    };
  }

  return {
    key: 'pending',
    chipColor: 'default',
    isHistorical: false,
  };
}
